import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Check, Loader2, CheckCircle2, X, Sparkles } from 'lucide-react';
import axios from 'axios';
import API_URL from '../../lib/apiUrl';
import { toast } from 'sonner';
import { Button } from '../ui/button';


// Compact list of common country dial codes (kept short for UX)
const COUNTRY_CODES = [
  { code: '+91', label: 'IN +91' },
  { code: '+1', label: 'US/CA +1' },
  { code: '+44', label: 'UK +44' },
  { code: '+61', label: 'AU +61' },
  { code: '+971', label: 'AE +971' },
  { code: '+65', label: 'SG +65' },
  { code: '+49', label: 'DE +49' },
  { code: '+33', label: 'FR +33' },
  { code: '+81', label: 'JP +81' },
  { code: '+86', label: 'CN +86' },
  { code: '+27', label: 'ZA +27' },
  { code: '+966', label: 'SA +966' },
  { code: '+34', label: 'ES +34' },
  { code: '+39', label: 'IT +39' },
  { code: '+55', label: 'BR +55' },
  { code: '+52', label: 'MX +52' },
  { code: '+7', label: 'RU +7' },
  { code: '+62', label: 'ID +62' },
  { code: '+60', label: 'MY +60' },
  { code: '+92', label: 'PK +92' },
  { code: '+880', label: 'BD +880' },
  { code: '+94', label: 'LK +94' },
  { code: '+64', label: 'NZ +64' },
  { code: '+90', label: 'TR +90' },
  { code: '+31', label: 'NL +31' },
  { code: '+46', label: 'SE +46' },
  { code: '+41', label: 'CH +41' },
];

const triggerDownload = (downloadUrl) => {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = 'ISO-27001-2022-Starter-Pack.zip';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const StarterPackLeadMagnet = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    country_code: '+91',
    phone: '',
  });

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!form.first_name.trim()) return 'First name is required';
    if (!form.last_name.trim()) return 'Last name is required';
    if (!form.email.trim()) return 'Email is required';
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return 'Please enter a valid email address';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        country_code: form.phone.trim() ? form.country_code : null,
        phone: form.phone.trim() || null,
      };
      const res = await axios.post(`${API_URL}/api/leads/starter-pack`, payload);
      const downloadUrl = res?.data?.download_url || '/downloads/ISO-27001-2022-Starter-Pack.zip';
      triggerDownload(downloadUrl);
      setSubmitted(true);
      toast.success('Your download has started');
    } catch (err) {
      console.error('Lead submission failed', err);
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    if (submitted) {
      setSubmitted(false);
      setForm({ first_name: '', last_name: '', email: '', country_code: '+91', phone: '' });
    }
  };

  return (
    <section
      id="starter-pack"
      className="relative overflow-hidden bg-[#0c1625] py-24 sm:py-32"
      data-testid="starter-pack-section"
    >
      {/* Subtle grid texture overlay (matches live hero) */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Pitch */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-emerald-300 bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Free Download
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-5 leading-tight tracking-tight">
              ISO 27001:2022{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(90deg, #6ee7b7 0%, #34d399 55%, #fbbf24 100%)',
                }}
              >
                Starter Pack
              </span>
            </h2>
            <p className="font-body text-lg text-slate-400 leading-relaxed mb-8 max-w-xl">
              A hand-picked bundle of templates, checklists and quick-reference guides to kickstart
              your ISO 27001 journey — no fluff, no email spam, just the essentials.
            </p>

            <div className="space-y-3 mb-10">
              {[
                'Information Security Policy template',
                'Risk register & treatment plan worksheets',
                'Annex A control implementation checklist',
                'Internal audit & management review templates',
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center bg-emerald-400/10 border border-emerald-400/30">
                    <Check className="h-3 w-3 text-emerald-300" />
                  </div>
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setShowForm(true)}
              size="lg"
              className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-emerald-500/10 hover:shadow-xl hover:shadow-emerald-500/20 transition-colors"
              data-testid="open-starter-pack-form-btn"
            >
              <Download className="mr-2 h-5 w-5" />
              Download the Starter Pack
            </Button>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mt-4">
              We respect your inbox · No spam ever
            </p>
          </motion.div>

          {/* Right: Glass card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div
              className="relative overflow-hidden rounded-xl p-8"
              style={{
                background: 'rgba(15, 29, 47, 0.72)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50" />
                    Available now
                  </div>
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
                    .zip · 350 KB
                  </span>
                </div>

                <div
                  className="rounded-md bg-[#111b2c] p-8 text-white"
                  style={{
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <FileText className="h-12 w-12 mb-4 text-emerald-300" />
                  <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-2">
                    ISO 27001:2022
                  </div>
                  <div className="font-heading font-semibold text-2xl text-white mb-2">
                    Starter Pack
                  </div>
                  <div className="text-sm text-slate-300">
                    Templates · Checklists · Quick-reference guides
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="font-heading font-bold text-2xl text-white">10+</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                      Templates
                    </div>
                  </div>
                  <div className="border-x border-white/10">
                    <div className="font-heading font-bold text-2xl text-white">93</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                      Controls
                    </div>
                  </div>
                  <div>
                    <div className="font-heading font-bold text-2xl text-white">Free</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                      Forever
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
          onClick={closeForm}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md rounded-xl bg-[#0f172a] p-8"
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 25px 60px -10px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="starter-pack-form-modal"
          >
            <button
              onClick={closeForm}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
              aria-label="Close"
              data-testid="close-starter-pack-form-btn"
            >
              <X className="h-5 w-5" />
            </button>

            {!submitted ? (
              <>
                <div className="mb-6">
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full mb-4">
                    <Sparkles className="h-3 w-3" />
                    Almost there
                  </span>
                  <h3 className="font-heading font-bold text-2xl text-white mb-2">
                    Get Your Starter Pack
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Tell us a bit about you and we'll start your download.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="starter-pack-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                        First Name <span className="text-emerald-300">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={(e) => updateField('first_name', e.target.value)}
                        required
                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                        placeholder="Jane"
                        data-testid="starter-pack-first-name-input"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                        Last Name <span className="text-emerald-300">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={(e) => updateField('last_name', e.target.value)}
                        required
                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                        placeholder="Doe"
                        data-testid="starter-pack-last-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                      Email <span className="text-emerald-300">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                      placeholder="you@company.com"
                      data-testid="starter-pack-email-input"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                      Phone <span className="text-slate-500 normal-case tracking-normal">(optional)</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={form.country_code}
                        onChange={(e) => updateField('country_code', e.target.value)}
                        className="px-2 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors max-w-[110px]"
                        data-testid="starter-pack-country-code-select"
                      >
                        {COUNTRY_CODES.map((cc) => (
                          <option key={cc.code} value={cc.code} className="bg-slate-900">
                            {cc.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => updateField('phone', e.target.value.replace(/[^0-9]/g, ''))}
                        className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                        placeholder="9876543210"
                        data-testid="starter-pack-phone-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg mt-2 shadow-lg shadow-emerald-500/10"
                    data-testid="starter-pack-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Preparing your download…
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Get the Starter Pack
                      </>
                    )}
                  </Button>

                  <p className="text-center text-[11px] leading-5 text-slate-500" data-testid="starter-pack-privacy-notice">
                    We use these details to deliver the resource and respond to related requests. See our <a href="/privacy" className="text-emerald-300 hover:text-emerald-200" data-testid="starter-pack-privacy-link">Privacy Policy</a>.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="starter-pack-success">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-400/10 border border-emerald-400/30">
                  <CheckCircle2 className="h-7 w-7 text-emerald-300" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">
                  Download Started!
                </h3>
                <p className="text-slate-400 text-sm mb-6">
                  Your <strong className="text-white">ISO 27001:2022 Starter Pack</strong> is on its
                  way. If it didn't start automatically, use the button below.
                </p>
                <a
                  href="/downloads/ISO-27001-2022-Starter-Pack.zip"
                  download="ISO-27001-2022-Starter-Pack.zip"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-900 px-5 py-3 rounded-lg text-sm font-semibold transition-colors"
                  data-testid="starter-pack-manual-download-btn"
                >
                  <Download className="h-4 w-4" />
                  Download Again
                </a>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default StarterPackLeadMagnet;
